/**
 * Task Actions Panel Component
 *
 * Displays available actions for the selected task.
 */

import type { Task } from "../lib/usePlanReasoning";

interface TaskActionsProps {
  task: Task;
  canAppend: boolean;
  onComplete: (taskId: string) => void;
  onClaim: (taskId: string) => void;
  onUnclaim: (taskId: string) => void;
}

export default function TaskActions({
  task,
  canAppend,
  onComplete,
  onClaim,
  onUnclaim,
}: TaskActionsProps) {
  const canClaim = task.status === "ready" && !task.agent;
  const canComplete = task.status === "in_progress" || task.status === "ready";
  const canUnclaim = task.status === "in_progress";

  // Show read-only notice if user can't append
  if (!canAppend) {
    return (
      <div className="space-y-3 text-sm">
        <div className="text-warning text-xs p-3 bg-warning/10 rounded border border-warning/20">
          <div className="font-medium mb-1">Read-only access</div>
          <div className="opacity-80">
            This URL only allows viewing. To claim or complete tasks, use an append or admin URL.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 text-sm">
      {canClaim && (
        <button
          onClick={() => onClaim(task.id)}
          className="btn btn-sm btn-primary w-full justify-start"
        >
          <span className="font-mono text-xs opacity-70 mr-2">hence claim</span>
          {task.id}
        </button>
      )}
      {canUnclaim && (
        <button
          onClick={() => onUnclaim(task.id)}
          className="btn btn-sm btn-ghost w-full justify-start"
        >
          <span className="font-mono text-xs opacity-70 mr-2">hence unclaim</span>
          {task.id}
        </button>
      )}
      {canComplete && (
        <button
          onClick={() => onComplete(task.id)}
          className="btn btn-sm btn-success w-full justify-start"
        >
          <span className="font-mono text-xs opacity-70 mr-2">hence complete</span>
          {task.id}
        </button>
      )}

      {/* Status messages */}
      {task.status === "blocked" && (
        <div className="text-warning text-xs p-2 bg-warning/10 rounded">
          Task is blocked. Resolve blockers first.
        </div>
      )}
      {task.status === "done" && (
        <div className="text-success text-xs p-2 bg-success/10 rounded">
          Task completed
        </div>
      )}
      {task.status === "backlog" && (
        <div className="text-base-content/50 text-xs p-2 bg-base-200 rounded space-y-1">
          <div>Waiting on dependencies to become ready</div>
          {task.blockedBy && task.blockedBy.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {task.blockedBy.map((dep) => (
                <span key={dep} className="badge badge-ghost badge-xs font-mono">{dep}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Command palette hint */}
      <div className="text-xs text-base-content/40 pt-2 border-t border-base-200">
        <kbd className="kbd kbd-xs">⇧⌘K</kbd> for more commands
      </div>
    </div>
  );
}
